import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Mail, ChevronLeft, Info } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

const LOGO = "https://content.webfactorysite.co.uk/20260113092111-Wales-Event-Logo.png?t=1768296071";

export default function StaffLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F2547] flex flex-col items-center justify-center p-4 sm:p-8">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-1 text-slate-300 hover:text-white text-sm mb-6 transition-colors" data-testid="back-to-portal-link">
          <ChevronLeft className="h-4 w-4" /> Back to portal
        </Link>
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="bg-[#F4F7FA] border-b border-slate-200 px-8 py-6 flex items-center gap-4">
            <img src={LOGO} alt="Wales Event Medical Services" className="h-12 w-auto" />
          </div>
          <form onSubmit={submit} className="px-8 py-8" data-testid="staff-login-form">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-[#0F2547] mb-1">Staff Sign In</h1>
            <p className="text-sm text-slate-500 mb-6">Clinical portal for event medical staff</p>

            {error && (
              <div className="mb-4 rounded-lg bg-red-50 border border-red-200 text-[#D5281B] text-sm px-4 py-3" data-testid="login-error">
                {error}
              </div>
            )}

            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email</label>
            <div className="relative mb-4">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                data-testid="staff-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-12 rounded-lg border border-slate-300 pl-10 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8] focus:border-[#005EB8]"
                placeholder="you@waleseventmedical.co.uk"
              />
            </div>

            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Password</label>
            <div className="relative mb-2">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                data-testid="staff-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full h-12 rounded-lg border border-slate-300 pl-10 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8] focus:border-[#005EB8]"
                placeholder="Your password"
              />
            </div>
            <div className="text-right mb-6">
              <Link to="/forgot-password" className="text-xs font-semibold text-[#005EB8] hover:underline" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>

            <button
              data-testid="staff-login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white font-semibold text-sm disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign In"}
            </button>

            <div className="mt-6 flex items-start gap-2 rounded-lg bg-sky-50 border border-sky-200 px-4 py-3">
              <Info className="h-4 w-4 text-[#005EB8] mt-0.5 shrink-0" />
              <p className="text-xs text-slate-600 leading-relaxed">
                Don't have a login? <span className="font-semibold text-[#0F2547]">Speak to your line manager</span> — they'll set up your account and access level.
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
